import { useState } from 'react';
import type { FormEvent, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { useUseInviteLink, useVerifyInviteLink } from '../../hooks/useCrewInviteLinks';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';

interface JoinWithInviteCodeProps {
    /** Called with the crewSlug after a successful join. Defaults to navigating to the crew page. */
    onSuccess?: (crewSlug: string) => void;
}

const JoinWithInviteCode = ({ onSuccess }: JoinWithInviteCodeProps) => {
    const navigate = useNavigate();
    const [inviteCode, setInviteCode] = useState('');
    const [error, setError] = useState('');

    const useInviteLinkMutation = useUseInviteLink();
    const verifyQuery = useVerifyInviteLink(inviteCode.trim());

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        const trimmedCode = inviteCode.trim();
        if (!trimmedCode) {
            setError('Please enter an invite code');
            return;
        }

        if (verifyQuery.isError) {
            setError('Invalid invite code. Please check and try again.');
            return;
        }

        if (verifyQuery.isLoading) return;

        if (!verifyQuery.data?.success || !verifyQuery.data.data) {
            setError('Unable to verify invite code');
            return;
        }

        setError('');

        try {
            const result = await useInviteLinkMutation.mutateAsync({ code: trimmedCode });
            if (result.success && result.data) {
                const crewSlug = result.data.crewSlug;
                if (onSuccess) {
                    onSuccess(crewSlug);
                } else {
                    navigate(`/c/${crewSlug}`);
                }
            }
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to join crew with this invite link');
        }
    };

    const handleCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInviteCode(e.target.value);
        if (error) setError('');
    };

    const isVerifying = verifyQuery.isFetching && inviteCode.trim().length > 0;
    const isJoining = useInviteLinkMutation.isPending;
    const isProcessing = isVerifying || isJoining;
    const crewInfo = verifyQuery.data?.success ? verifyQuery.data.data : null;

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <TextField
                fullWidth
                label="Invite Code"
                placeholder="Enter your invite code"
                value={inviteCode}
                onChange={handleCodeChange}
                error={!!error || verifyQuery.isError}
                helperText={
                    error ||
                    (verifyQuery.isError
                        ? 'Invalid invite code'
                        : 'A member of the crew should have shared this code with you.')
                }
                size="small"
                sx={{ mb: 2 }}
            />

            {crewInfo && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    Ready to join <strong>{crewInfo.crewName}</strong>
                </Alert>
            )}

            {error && !verifyQuery.isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={!inviteCode.trim() || isProcessing || verifyQuery.isError || !crewInfo}
                startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
                {isVerifying ? 'Verifying...' : isJoining ? 'Joining...' : crewInfo ? 'Join Crew' : 'Continue'}
            </Button>
        </Box>
    );
};

export default JoinWithInviteCode;
